import React, { useState, useContext, useEffect, useRef } from "react";
import {
  Container,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { AuthContext } from "../../../ExtraPages/AuthProvider";
import DataTable from "react-data-table-component";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import axios from 'axios';
import jsQR from 'jsqr';
import { getIdGivenUserId } from '../../../../api/processing plant/plantApi';
import {
  faQrcode,
  faPlus,
  faUserPlus,
  faStop,
  faSave,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

const Drivers = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [modal, setModal] = useState(false);
  const [userId, setUserId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [vin, setVin] = useState('');
  const [vehicleColor, setVehicleColor] = useState('');
  const [vehicleRegYear, setVehicleRegYear] = useState('');
  const [email, setEmail] = useState('');
  const [contactNumber, setContactNumber] = useState('');
  const [scanning, setScanning] = useState(false);
  const { user } = useContext(AuthContext);
  const scannerRef = useRef(null);
  const [plantId, setPlantId] = useState([]);
  const [cameraPermission, setCameraPermission] = useState(null);
  const [cameraError, setCameraError] = useState('');
  const [manualInput, setManualInput] = useState('');
  const [showManualInput, setShowManualInput] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const toggleModal = () => {
    setModal(!modal);
    setSelectedDriver('');
    setScanning(true);
    stopScanner();
    setMessage('');
    setCameraPermission(null);
    setCameraError('');
    setUserId('');
    setName('');
    setVin('');
    setVehicleColor('');
    setVehicleRegYear('');
    setEmail('');
    setContactNumber('');
    setManualInput('');
    setShowManualInput(false);
    setScanResult(null);
  };

  useEffect(() => {
    const fetchPlantId = async () => {
      try {
        if (user?.uid) {
          const plantId = await getIdGivenUserId(user.uid);
          console.log('plantId', plantId.data);
          setPlantId(plantId.data);
        }
      } catch (error) {
        console.error('Error fetching plant ID:', error);
        setMessage('Failed to fetch plant ID.');
      }
    };
    fetchPlantId();
  }, [user]);

  const fetchDrivers = async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const response = await axios.get(
        `https://api.starmarketapi.online/truck_driver1/truckDriverByPlant/${plantId}`
      );
      const driverData = Array.isArray(response.data.data)
        ? response.data.data
        : [];
      setDrivers(driverData);
    } catch (error) {
      console.error('Error fetching drivers:', error);
      setMessage('Error fetching drivers data');
      setDrivers([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
  }, [plantId]);

  const handleUpdateCompany = async () => {
    if (!userId || !name || !companyName) {
      setMessage('User ID, Name, and Company Name are required.');
      return;
    }

    setLoading(true);
    setMessage('');
    try {
      await axios.post(
        'https://api.starmarketapi.online/truck_driver1/truckDriver/upsert',
        {
          userId,
          companyName: plantId,
          firstName: name,
          lastName: '',
          vin,
          vehicleColor,
          vehicleRegYear,
          email,
          contactNumber,
        }
      );
      setMessage('Driver added successfully!');
      fetchDrivers();
    } catch (error) {
      setMessage('Error adding driver. Please try again.');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const startScanner = async () => {
    if (cameraPermission === null) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        streamRef.current = stream;
        setCameraPermission(true);
        setMessage('Camera access granted.');
      } catch (err) {
        console.error('Camera permission error:', err);
        setCameraPermission(false);
        setCameraError(err.message);
        setMessage(
          `Camera access denied: ${err.message}. Please allow camera access.`
        );
        return;
      }
    }

    if (cameraPermission === false) {
      setMessage('Camera access denied. Please enable camera permissions.');
      return;
    }

    try {
      if (!streamRef.current) {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        streamRef.current = stream;
      }
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play();
      const canvas = canvasRef.current;
      const canvasContext = canvas.getContext('2d');

      const scanFrame = () => {
        if (
          videoRef.current &&
          videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA &&
          scanning
        ) {
          canvas.height = videoRef.current.videoHeight;
          canvas.width = videoRef.current.videoWidth;
          canvasContext.drawImage(
            videoRef.current,
            0,
            0,
            canvas.width,
            canvas.height
          );
          const imageData = canvasContext.getImageData(
            0,
            0,
            canvas.width,
            canvas.height
          );
          const code = jsQR(imageData.data, imageData.width, imageData.height, {
            inversionAttempts: 'dontInvert',
          });
          if (code) {
            handleScan({ text: code.data });
          }
        }
        if (scanning) {
          requestAnimationFrame(scanFrame);
        }
      };
      scanFrame();
    } catch (err) {
      console.error('Scanner error:', err);
      setMessage(`Failed to start scanner: ${err.message}`);
      setCameraError(err.message);
    }
  };

  const stopScanner = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
  };

  const handleScan = (data) => {
    if (!data || !data.text) {
      console.log('No QR code data scanned.');
      setMessage('No QR code data detected. Please scan a valid QR code.');
      return;
    }
    console.log('Scanned QR Code Data:', data.text);
    setScanResult(data.text);
    try {
      let userId, name, vin, vehicleColor, vehicleRegYear, email, contactNumber;
      if (data.text.includes(',')) {
        const tokens = data.text.split(',');
        userId = tokens[0]?.trim();
        name = tokens[1]?.trim();
        vin = tokens[2]?.trim();
        vehicleColor = tokens[3]?.trim();
        vehicleRegYear = tokens[4]?.trim();
        email = tokens[5]?.trim();
        contactNumber = tokens[6]?.trim();
      } else {
        const parsed = JSON.parse(data.text);
        userId = parsed.userId || parsed.id;
        name = parsed.name || parsed.fullName;
        vin = parsed.vin;
        vehicleColor = parsed.vehicleColor;
        vehicleRegYear = parsed.vehicleRegYear;
        email = parsed.email;
        contactNumber = parsed.contactNumber;
      }
      if (userId && name) {
        setUserId(userId);
        setName(name);
        setVin(vin || '');
        setVehicleColor(vehicleColor || '');
        setVehicleRegYear(vehicleRegYear || '');
        setEmail(email || '');
        setContactNumber(contactNumber || '');
        setMessage('QR code scanned successfully!');
        setScanning(false);
        stopScanner();
      } else {
        setMessage(
          'Invalid QR code format. Expected: userId,name,vin,vehicleColor,vehicleRegYear,email,contactNumber or JSON.'
        );
      }
    } catch (err) {
      console.error('QR code parsing error:', err);
      setMessage(
        "Error parsing QR code data. Ensure it's in the correct format."
      );
    }
  };

  useEffect(() => {
    if (!user) return;
    setCompanyName(user.uid);
  }, [user]);

  useEffect(() => {
    if (scanning && cameraPermission !== false) {
      startScanner();
    } else {
      stopScanner();
    }
  }, [scanning, cameraPermission]);

  const handleManualInputSubmit = () => {
    if (!manualInput) {
      setMessage('Please enter the QR code data.');
      return;
    }
    try {
      let userId, name, vin, vehicleColor, vehicleRegYear, email, contactNumber;
      if (manualInput.includes(',')) {
        const tokens = manualInput.split(',');
        userId = tokens[0]?.trim();
        name = tokens[1]?.trim();
        vin = tokens[2]?.trim();
        vehicleColor = tokens[3]?.trim();
        vehicleRegYear = tokens[4]?.trim();
        email = tokens[5]?.trim();
        contactNumber = tokens[6]?.trim();
      } else {
        const parsed = JSON.parse(manualInput);
        userId = parsed.userId || parsed.id;
        name = parsed.name || parsed.fullName;
        vin = parsed.vin;
        vehicleColor = parsed.vehicleColor;
        vehicleRegYear = parsed.vehicleRegYear;
        email = parsed.email;
        contactNumber = parsed.contactNumber;
      }
      if (userId && name) {
        setUserId(userId);
        setName(name);
        setVin(vin || '');
        setVehicleColor(vehicleColor || '');
        setVehicleRegYear(vehicleRegYear || '');
        setEmail(email || '');
        setContactNumber(contactNumber || '');
        setMessage('Manual input successful!');
        setShowManualInput(false);
      } else {
        setMessage(
          'Invalid format. Expected: userId,name,vin,vehicleColor,vehicleRegYear,email,contactNumber or JSON.'
        );
      }
    } catch (err) {
      console.error('Manual input parsing error:', err);
      setMessage(
        "Error parsing manual input. Ensure it's in the correct format."
      );
    }
  };

  const handleSave = () => {
    if (userId && name) {
      handleUpdateCompany();
      toggleModal();
    } else {
      setMessage('Please provide at least User ID and Name.');
    }
  };

  const columns = [
    { name: '#', selector: (row, index) => index + 1, sortable: true },
    {
      name: 'Name',
      selector: (row) => row.firstName + ' ' + row.lastName,
      sortable: true,
    },
    { name: 'VIN', selector: (row) => row.vin || 'N/A', sortable: true },
    {
      name: 'Vehicle Color',
      selector: (row) => row.vehicleColor || 'N/A',
      sortable: true,
    },
    {
      name: 'Reg Year',
      selector: (row) => row.vehicleRegYear || 'N/A',
      sortable: true,
    },
    { name: 'Email', selector: (row) => row.email || 'N/A', sortable: true },
    {
      name: 'Contact',
      selector: (row) => row.contactNumber || 'N/A',
      sortable: true,
    },
    { name: 'Created At', selector: (row) => row.createdAt, sortable: true },
  ];

  const previewStyle = {
    height: 300,
    width: 300,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 auto',
    border: '2px solid #007bff',
    borderRadius: '8px',
    overflow: 'hidden',
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ minHeight: '100%' }}>
        <Container fluid>
          <Card style={{ backgroundColor: '#fff' }}>
            <CardBody style={{ padding: '20px' }}>
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h5 className="mb-0">Drivers</h5>
                <Button
                  color="success"
                  onClick={toggleModal}
                >
                  <FontAwesomeIcon
                    icon={faPlus}
                    className="mr-2"
                  />
                  Add Driver
                </Button>
              </div>
              <DataTable
                columns={columns}
                data={drivers}
                pagination
                progressPending={loading}
                noDataComponent="No drivers found"
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal
        isOpen={modal}
        toggle={toggleModal}
        className="custom-modal"
      >
        <ModalHeader
          toggle={toggleModal}
          className="bg-primary text-white"
          cssModule={{ 'modal-title': 'w-100 text-center' }}
        >
          <FontAwesomeIcon
            icon={faUserPlus}
            className="mr-2"
          />
          Add New Driver
        </ModalHeader>

        <ModalBody className="p-4">
          <Form
            onSubmit={(event) => event.preventDefault()}
            className="d-flex flex-column align-items-center"
          >
            <Button
              color={scanning ? 'danger' : 'success'}
              onClick={() => setScanning(!scanning)}
              disabled={cameraPermission === false}
              className="mb-3 d-flex align-items-center"
              size="lg"
            >
              {scanning ? (
                <>
                  <FontAwesomeIcon
                    icon={faStop}
                    className="mr-2"
                  />
                  Stop Scanning
                </>
              ) : (
                <>
                  <FontAwesomeIcon
                    icon={faQrcode}
                    className="mr-2"
                  />
                  Scan QR Code
                </>
              )}
            </Button>

            {scanning && cameraPermission && (
              <div style={previewStyle}>
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  style={{ width: '100%', height: '100%' }}
                />
                <canvas
                  ref={canvasRef}
                  style={{ display: 'none' }}
                />
              </div>
            )}

            <FormGroup className="mt-3 w-100">
              <Label
                for="userId"
                className="font-weight-bold"
              >
                User ID
              </Label>
              <Input
                type="text"
                id="userId"
                placeholder="Enter User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="name"
                className="font-weight-bold"
              >
                Name
              </Label>
              <Input
                type="text"
                id="name"
                placeholder="Enter Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="vin"
                className="font-weight-bold"
              >
                VIN
              </Label>
              <Input
                type="text"
                id="vin"
                placeholder="Enter Vehicle VIN"
                value={vin}
                onChange={(e) => setVin(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="vehicleColor"
                className="font-weight-bold"
              >
                Vehicle Color
              </Label>
              <Input
                type="text"
                id="vehicleColor"
                placeholder="Enter Vehicle Color"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="vehicleRegYear"
                className="font-weight-bold"
              >
                Vehicle Registration Year
              </Label>
              <Input
                type="number"
                id="vehicleRegYear"
                placeholder="Enter Registration Year"
                value={vehicleRegYear}
                onChange={(e) => setVehicleRegYear(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="email"
                className="font-weight-bold"
              >
                Email
              </Label>
              <Input
                type="email"
                id="email"
                placeholder="Enter Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            <FormGroup className="mt-3 w-100">
              <Label
                for="contactNumber"
                className="font-weight-bold"
              >
                Contact Number
              </Label>
              <Input
                type="tel"
                id="contactNumber"
                placeholder="Enter Contact Number"
                value={contactNumber}
                onChange={(e) => setContactNumber(e.target.value)}
                disabled={scanning}
                className="shadow-sm"
                style={{ borderRadius: '6px', padding: '10px' }}
              />
            </FormGroup>

            {message && (
              <p
                className="text-center mt-2"
                style={{ color: 'red' }}
              >
                {message}
              </p>
            )}
            {cameraError && (
              <p
                className="text-center mt-2"
                style={{ color: 'orange' }}
              >
                {/* Camera Error: {cameraError} */}
              </p>
            )}
          </Form>
        </ModalBody>

        <ModalFooter className="bg-light border-top">
          <Button
            color="primary"
            onClick={handleSave}
            disabled={!userId || !name || loading}
            className="px-4 py-2 shadow-sm"
            style={{ borderRadius: '6px' }}
          >
            <FontAwesomeIcon
              icon={faSave}
              className="mr-2"
            />{' '}
            Save
          </Button>
          <Button
            color="secondary"
            onClick={toggleModal}
            className="px-4 py-2 shadow-sm"
            style={{ borderRadius: '6px' }}
          >
            <FontAwesomeIcon
              icon={faTimes}
              className="mr-2"
            />{' '}
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </motion.div>
  );
};

export default Drivers;